import React from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCode, FaChartLine, FaFileAlt, FaLaptopCode, FaTools, FaRocket } from 'react-icons/fa';
import { BiNetworkChart, BiData } from 'react-icons/bi';
import { IoAnalyticsSharp, IoSpeedometerSharp } from 'react-icons/io5';
import { BsGraphUp, BsThreeDots } from 'react-icons/bs';
// Removed unused MUI imports

export default function Home() {
    const [hoveredOption, setHoveredOption] = useState(null);
    const navigate = useNavigate();
    const options = [
        {
          id: 1,
          title: 'Creation from Scratch',
          description: 'Build your virtual object from the ground up with our intuitive creation tools.',
          icon: <FaCode size={50} className="option-icon-svg" />,
          features: ['Visual Modeling', 'Integration Setup', 'Custom Attributes'],
          link: '/create-from-scratch',
        },
        {
          id: 2,
          title: 'Management',
          description: 'Monitor, update, and optimize your existing virtual object in real-time.',
          icon: <BiNetworkChart size={50} className="option-icon-svg" />,
          features: ['Live Monitoring', 'Performance Analytics', 'System Alerts'],
          link: '/management',
        },
        {
          id: 3,
          title: 'Templates',
          description: 'Choose from pre-built templates to quickly deploy industry-specific virtual object.',
          icon: <FaFileAlt size={50} className="option-icon-svg" />,
          features: ['Industry Standards', 'Quick Deployment', 'Customizable'],
          link: '/templates',
        }
      ];
    return (
        <main>
        <section className="hero">
          <div className="hero-content">
            <h1>Virtual Object Management Platform</h1>
            <p>Create, manage, and optimize digital replicas of your physical assets with industry-leading technology</p>
            <div className="hero-buttons">
              <button className="cta-btn primary-cta" onClick={() => navigate('/create-from-scratch')} aria-label="Get started with VirtuMesh"><FaRocket className="btn-icon" aria-hidden="true" /> Get Started</button>
              <button className="cta-btn secondary-cta"  onClick={() => window.open('https://www.youtube.com/', '_blank')} aria-label="Watch live demo"><FaLaptopCode className="btn-icon" aria-hidden="true" /> Live Demo</button>
            </div>
          </div>
          <div className="hero-image">
            <div className="digital-twin-visualization">
              <div className="visualization-label physical-label">Physical Asset</div>
              <div className="physical-asset"></div>
              <div className="connection-lines">
                <div className="data-point p1" aria-hidden="true"><IoSpeedometerSharp /></div>
                <div className="data-point p2" aria-hidden="true"><BsGraphUp /></div>
                <div className="data-point p3" aria-hidden="true"><IoAnalyticsSharp /></div>
                <div className="data-stream" aria-hidden="true"></div>
              </div>
              <div className="digital-asset"></div>
              <div className="visualization-label digital-label">Virtual Object</div>
            </div>
          </div>
        </section>

        <section className="stats-banner" aria-label="Platform statistics">
          <div className="stat-item">
            <div className="stat-icon" aria-hidden="true"><BsGraphUp /></div>
            <div className="stat-number">87%</div>
            <div className="stat-text">Increased Efficiency</div>
          </div>
          <div className="stat-item">
            <div className="stat-icon" aria-hidden="true"><IoAnalyticsSharp /></div>
            <div className="stat-number">65%</div>
            <div className="stat-text">Reduced Downtime</div>
          </div>
          <div className="stat-item">
            <div className="stat-icon" aria-hidden="true"><FaTools /></div>
            <div className="stat-number">5000+</div>
            <div className="stat-text">Assets Managed</div>
          </div>
        </section>

        <section className="options" id="options">
          <h2>Choose Your Path</h2>
          <div className="options-container">
            {options.map((option) => (
              <div
                key={option.id}
                className={`option-card ${hoveredOption === option.id ? 'hovered' : ''}`}
                onMouseEnter={() => setHoveredOption(option.id)}
                onMouseLeave={() => setHoveredOption(null)}
                onClick={() => navigate(option.link)} // Navigate to the selected option
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(option.link); }}
                aria-label={`Select ${option.title}: ${option.description}`}
              >
                <div className="option-icon" aria-hidden="true">
                  {option.icon}
                </div>
                <h3>{option.title}</h3>
                <p>{option.description}</p>
                <ul className="option-features">
                  {option.features.map((feature, index) => (
                    <li key={index}><BsThreeDots aria-hidden="true" /> {feature}</li>
                  ))}
                </ul>
                <button className="option-btn" aria-label={`Navigate to ${option.title}`}>Select <span aria-hidden="true">&rarr;</span></button>
              </div>
            ))}
          </div>
        </section>

        <section className="how-it-works" id="how-it-works">
          <h2>How It Works</h2>
          <div className="steps-container">
            <div className="step">
              <div className="step-number" aria-hidden="true">1</div>
              <div className="step-icon" aria-hidden="true"><FaCode /></div>
              <h3>Model Creation</h3>
              <p>Create a digital model that mirrors your physical asset's characteristics and behaviors</p>
            </div>
            <div className="step-connector" aria-hidden="true"></div>
            <div className="step">
              <div className="step-number" aria-hidden="true">2</div>
              <div className="step-icon" aria-hidden="true"><BiNetworkChart /></div>
              <h3>Data Integration</h3>
              <p>Connect real-time data sources to synchronize physical and digital entities</p>
            </div>
            <div className="step-connector" aria-hidden="true"></div>
            <div className="step">
              <div className="step-number" aria-hidden="true">3</div>
              <div className="step-icon" aria-hidden="true"><IoAnalyticsSharp /></div>
              <h3>Analysis & Optimization</h3>
              <p>Leverage insights from your digital twin to optimize operations and predict maintenance</p>
            </div>
          </div>
        </section>

        <section className="features" id="features">
          <h2>Key Features</h2>
          <div className="features-grid">
            <div className="feature">
              <div className="feature-icon" aria-hidden="true"><FaChartLine /></div>
              <h3>Real-time Monitoring</h3>
              <p>Track your physical assets with live data updates</p>
            </div>
            <div className="feature">
              <div className="feature-icon" aria-hidden="true"><IoSpeedometerSharp /></div>
              <h3>Predictive Maintenance</h3>
              <p>Anticipate issues before they occur</p>
            </div>
            <div className="feature">
              <div className="feature-icon" aria-hidden="true"><BiData /></div>
              <h3>Data Analytics</h3>
              <p>Gain insights from comprehensive data analysis</p>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <div className="cta-content">
            <h2>Ready to Transform Your Operations?</h2>
            <p>Join hundreds of industry leaders already using our platform</p>
            <button className="cta-btn primary-cta" onClick={() => navigate('/create-from-scratch')} aria-label="Get started with VirtuMesh today">Get Started Today</button>
          </div>
        </section>
      </main>
    );
}
